import { HEXColor } from '@neptune/colors-domain';

import {
  getTableWidgetPointInterpolationFlag,
  getTableWidgetPointValue,
  getTableWidgetPointX,
} from './points';
import { TableWidgetSeriesResult } from './table-widget-series-result';

export type TableWidgetRow = {
  attributeName: string;
  color: HEXColor;
  displayName: string;
  entityId: string;
  entityOrderKey: number;
  key: string;
  metricOrderKey: number;
  projectIdentifier: string;
  shortId: string;
  strokePattern?: number[];
  valuesByStep: Partial<Record<number, number>>;
};

const TARGET_STEP_POINT_INDEX = 1;

export function buildTableWidgetRows({
  visibleSteps,
  seriesData,
}: {
  visibleSteps: number[];
  seriesData: TableWidgetSeriesResult[];
}): TableWidgetRow[] {
  return seriesData
    .map((series) => ({
      attributeName: series.metric.parsedExpression.attributes[0] ?? series.metric.label,
      color: series.color,
      displayName: series.shortId,
      entityId: series.holderIdentifier,
      entityOrderKey: series.entityOrderKey,
      key: series.metric.customId,
      metricOrderKey: series.metricOrderKey,
      projectIdentifier: series.projectIdentifier,
      shortId: series.shortId,
      strokePattern: series.strokePattern,
      valuesByStep: extractExactStepValues(series.points, visibleSteps),
    }))
    .sort(compareTableWidgetRows);
}

export function compareTableWidgetRows(left: TableWidgetRow, right: TableWidgetRow): number {
  if (left.entityOrderKey !== right.entityOrderKey) {
    return left.entityOrderKey - right.entityOrderKey;
  }

  if (left.metricOrderKey !== right.metricOrderKey) {
    return left.metricOrderKey - right.metricOrderKey;
  }

  return left.key.localeCompare(right.key);
}

export function extractExactStepValue(
  points: TableWidgetSeriesResult['points'],
  selectedStep?: number,
): number | undefined {
  const point =
    selectedStep === undefined
      ? findLatestExactPoint(points)
      : findPreferredPointAtStep(points, selectedStep);

  if (!point) {
    return undefined;
  }

  const value = getTableWidgetPointValue(point);

  return Number.isFinite(value) ? value : undefined;
}

export function extractExactStepValues(
  points: TableWidgetSeriesResult['points'],
  steps: number[],
): Partial<Record<number, number>> {
  if (steps.length === 0 || points.length === 0) {
    return {};
  }

  const requestedSteps = new Set(steps);
  const valuesByStep = new Map<number, number>();

  for (let index = points.length - 1; index >= 0; index -= 1) {
    const point = points[index];
    const step = getTableWidgetPointX(point);

    if (!requestedSteps.has(step) || valuesByStep.has(step) || !isExactPoint(point)) {
      continue;
    }

    const value = getTableWidgetPointValue(point);

    if (Number.isFinite(value)) {
      valuesByStep.set(step, value);
    }
  }

  const preferredPoint = points[TARGET_STEP_POINT_INDEX];

  if (preferredPoint) {
    const preferredStep = getTableWidgetPointX(preferredPoint);
    const preferredValue = getTableWidgetPointValue(preferredPoint);

    if (
      requestedSteps.has(preferredStep) &&
      isExactPoint(preferredPoint) &&
      Number.isFinite(preferredValue)
    ) {
      valuesByStep.set(preferredStep, preferredValue);
    }
  }

  const result: Partial<Record<number, number>> = {};

  steps.forEach((step) => {
    const value = valuesByStep.get(step);

    if (value !== undefined) {
      result[step] = value;
    }
  });

  return result;
}

export function hasTableWidgetValue(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value);
}

export function getTableWidgetExactSteps(seriesData: TableWidgetSeriesResult[]): number[] {
  const exactSteps = new Set<number>();

  for (const series of seriesData) {
    for (const point of series.points) {
      if (isExactPoint(point)) {
        exactSteps.add(getTableWidgetPointX(point));
      }
    }
  }

  return Array.from(exactSteps).sort((left, right) => left - right);
}

function findPreferredPointAtStep(
  points: TableWidgetSeriesResult['points'],
  selectedStep: number,
): TableWidgetSeriesResult['points'][number] | undefined {
  const middlePoint = points[TARGET_STEP_POINT_INDEX];

  if (
    middlePoint &&
    getTableWidgetPointX(middlePoint) === selectedStep &&
    isExactPoint(middlePoint)
  ) {
    return middlePoint;
  }

  return findExactPointAtStep(points, selectedStep);
}

function findExactPointAtStep(
  points: TableWidgetSeriesResult['points'],
  selectedStep: number,
): TableWidgetSeriesResult['points'][number] | undefined {
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const point = points[index];

    if (getTableWidgetPointX(point) === selectedStep && isExactPoint(point)) {
      return point;
    }
  }

  return undefined;
}

function findLatestExactPoint(
  points: TableWidgetSeriesResult['points'],
): TableWidgetSeriesResult['points'][number] | undefined {
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const point = points[index];

    if (isExactPoint(point)) {
      return point;
    }
  }

  return undefined;
}

function isExactPoint(point: TableWidgetSeriesResult['points'][number]) {
  return !getTableWidgetPointInterpolationFlag(point);
}
